import type { ButtonHTMLAttributes, ReactElement } from 'react';

/**
 * Schaltfläche in den drei Ausprägungen der Anwendung (§40.4).
 *
 * Bis S2 stand dieselbe Klassenkette an einem Dutzend Stellen. Mit S3 kommen die
 * Teilnehmeransichten hinzu — spätestens dort wäre die Wiederholung nicht mehr zu überblicken, und
 * §40.4 verlangt ausdrücklich eigene wiederverwendbare Komponenten mit zentraler Anpassbarkeit.
 *
 * `min-h-touchflaeche` ist fest eingebaut und nicht überschreibbar: §19.1 verlangt ausreichend
 * große Touchflächen, und das darf keine Frage der Sorgfalt an der einzelnen Aufrufstelle sein.
 */

export type KnopfArt = 'primaer' | 'sekundaer' | 'gefahr';

/**
 * Jede Ausprägung hat eine sichtbare Zeigerrückmeldung. Fehlt sie bei einzelnen Schaltflächen,
 * wirken diese wie deaktiviert — deshalb steht sie hier zentral und nicht an den Aufrufstellen.
 * Bei den Farbflächen wird die Helligkeit verändert statt eine zweite Farbe eingeführt: das wirkt
 * in beiden Erscheinungsbildern (§21) und kommt ohne zusätzliche Designtokens aus.
 */
const KLASSEN: Record<KnopfArt, string> = {
  primaer: 'bg-akzent text-akzent-text hover:brightness-110',
  sekundaer: 'border-rand bg-flaeche-gedaempft hover:bg-rand border',
  gefahr: 'bg-fehler text-white hover:brightness-110',
};

export interface KnopfProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  art?: KnopfArt;
  /** Über die volle Breite — für die großflächige Bedienung der Teilnehmeransichten (§19.1). */
  breit?: boolean;
}

export function Knopf({
  art = 'primaer',
  breit = false,
  className,
  type = 'button',
  ...rest
}: KnopfProps): ReactElement {
  const klassen = [
    // `disabled:pointer-events-none` hält den Hover-Effekt von deaktivierten Schaltflächen fern —
    // sonst führte der Zeiger eine Bedienbarkeit vor, die gerade nicht besteht.
    'min-h-touchflaeche rounded-md px-4 font-medium transition',
    'disabled:pointer-events-none disabled:opacity-60',
    KLASSEN[art],
    breit ? 'w-full' : '',
    className ?? '',
  ]
    .filter((teil) => teil !== '')
    .join(' ');

  // `type` ist oben mit 'button' vorbelegt: eine Schaltfläche ohne ausdrücklichen Typ löste sonst
  // in einem Formular ungewollt das Absenden aus.
  return <button type={type} className={klassen} {...rest} />;
}
